import React, { Component } from 'react';
import { Grid, Button, Header, Modal, Input, Select } from 'semantic-ui-react';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import * as _ from 'lodash';

import { createToast, toastType } from 'Common/ToastHelper';
import { DirtyWrapper } from 'Components/tracked/DirtyWrapper';

import 'Styles/components/mappingmodal.less';

class MappingModal extends Component {
  static propTypes = {
    title: PropTypes.string,
    saveCallback: PropTypes.func,
    closeCallback: PropTypes.func,
    showDialog: PropTypes.bool.isRequired,
    data: PropTypes.array.isRequired,
    mappingOptions: PropTypes.array.isRequired,
    sourceOptions: PropTypes.array,
    textBoxMapping: PropTypes.bool,
  };

  static defaultProps = {
    textBoxMapping: false,
  };

  state = {
    mappingData: [],
    initialData: [],
    sourceValue: '',
    mappedValue: '',
  };

  columns = [
    {
      Header: 'Source Value',
      id: 'Source Value',
      accessor: rowData => {
        if (!this.props.sourceOptions) {
          return rowData.key;
        }
        const foundItem = this.props.sourceOptions.find(
          // Explicit string conversion, sometimes these aren't the same type.
          element => `${element.value}` === `${rowData.key}`
        );
        if (foundItem) {
          return foundItem.text;
        }
        return rowData.key;
      },
    },
    {
      Header: 'Mapped Value',
      id: 'Mapped Value',
      accessor: rowData => {
        const foundItem = this.props.mappingOptions.find(
          // Explicit string conversion, sometimes these aren't the same type.
          element => `${element.value}` === `${rowData.value}`
        );
        if (foundItem) {
          return foundItem.text;
        }
        return rowData.value;
      },
    },
    {
      Header: '',
      id: 'Delete',
      accessor: rowData => (
        <Button onClick={this.deleteRow(rowData.key)} size="mini">
          Remove
        </Button>
      ),
      className: 'lite-padding',
      minWidth: 28,
    },
  ];

  deleteRow = key => () => {
    const { mappingData } = this.state;
    let updatedData = _.cloneDeep(mappingData);
    updatedData.splice(
      updatedData.findIndex(row => row.key === key),
      1
    );

    this.setState({
      mappingData: updatedData,
    });
  };

  componentDidMount() {
    const { data } = this.props;

    this.setState({
      mappingData: data,
      initialData: data,
      sourceValue: '',
      mappedValue: '',
    });
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.showDialog && this.props.showDialog) {
      this.setState({
        mappingData: this.props.data,
        initialData: this.props.data,
        sourceValue: '',
        mappedValue: '',
      });
    }
  }

  onSourceValueChange = (e, { value }) => {
    this.setState({ sourceValue: value });
  };

  onMappedValueChange = (e, { value }) => {
    this.setState({ mappedValue: value });
  };

  onClear = () => {
    this.setState({ sourceValue: '', mappedValue: '' });
  };

  onAdd = () => {
    const { mappingData, mappedValue } = this.state;
    let { sourceValue } = this.state;

    if (typeof sourceValue === 'string') {
      sourceValue = sourceValue.trim();
    }

    if (
      mappingData.findIndex(
        row => row.key.toUpperCase() === `${sourceValue}`.toUpperCase()
      ) !== -1
    ) {
      createToast(toastType.error, 'Error', 'Source Value is already mapped.');
      return;
    }

    //note that by casting mappedValue to a string, we're no longer catching
    //the case when value = 0 (seen most often from makeOptionsDataWithEmpty).
    //the easiest way to avoid this is to just use makeOptionsData (or
    //makeOptionsDataRemoveEmpty) instead, and just use the clear button instead
    //of letting users choose an empty value.
    if (!sourceValue || !`${mappedValue}`) {
      createToast(
        toastType.error,
        'Error',
        'Source Value and Mapped Value are required.'
      );
      return;
    }

    let updatedData = _.cloneDeep(mappingData);

    this.setState({
      mappingData: [{ key: `${sourceValue}`, value: mappedValue }].concat(
        updatedData
      ),
      sourceValue: '',
      mappedValue: '',
    });
  };

  render() {
    const {
      showDialog,
      title,
      sourceOptions,
      mappingOptions,
      saveCallback,
      closeCallback,
      textBoxMapping,
    } = this.props;
    const { mappingData, initialData, sourceValue, mappedValue } = this.state;

    const filteredSourceOptions = sourceOptions
      ? sourceOptions.filter(
          option => !mappingData.find(mapped => mapped.key === option.value)
        )
      : null;

    return (
      <DirtyWrapper
        closeModalYesCallback={closeCallback}
        manual
        original={initialData}
        modified={mappingData}
        ignorePageLevelDirty
      >
        {showDirtyConfirm => (
          <Modal
            open={showDialog}
            size="small"
            onClose={showDirtyConfirm}
            closeIcon
            closeOnDimmerClick={false}
          >
            <Header icon="info circle" content={title} />
            <Modal.Content className={'mapping-modal'}>
              <Grid relaxed>
                <Grid.Row>
                  <Grid.Column>
                    <ReactTable
                      className={'grid--fixed-row-height'}
                      columns={this.columns}
                      minRows={5}
                      data={mappingData}
                      showPageSizeOptions={false}
                      pageSize={5}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row stretched centered columns={2}>
                  <Grid.Column width={6} textAlign={'center'}>
                    <label>Source Value</label>
                    {sourceOptions ? (
                      <Select
                        options={filteredSourceOptions}
                        onChange={this.onSourceValueChange}
                        value={sourceValue}
                      />
                    ) : (
                      <Input
                        maxLength={20}
                        onChange={this.onSourceValueChange}
                        value={sourceValue}
                      />
                    )}
                  </Grid.Column>
                  <Grid.Column width={6} textAlign={'center'}>
                    <label>Mapped Value</label>
                    {textBoxMapping ? (
                      <Input
                        maxLength={20}
                        onChange={this.onMappedValueChange}
                        value={mappedValue}
                      />
                    ) : (
                      <Select
                        options={mappingOptions}
                        onChange={this.onMappedValueChange}
                        value={mappedValue}
                      />
                    )}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row centered columns={1}>
                  <Grid.Column textAlign={'center'}>
                    <Button size="small" onClick={this.onAdd}>
                      Add
                    </Button>
                    <Button size="small" onClick={this.onClear}>
                      Clear
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Modal.Content>
            <Modal.Actions>
              <Button
                className={'pull-right margin-bottom'}
                inverted
                color="orange"
                size="big"
                content="Save"
                onClick={() => saveCallback(mappingData)}
              />
              <Button
                inverted
                color="orange"
                size="big"
                content="Cancel"
                className={'pull-right margin-bottom'}
                onClick={showDirtyConfirm}
              />
            </Modal.Actions>
          </Modal>
        )}
      </DirtyWrapper>
    );
  }
}

export { MappingModal };



// WEBPACK FOOTER //
// ./src/components/dialogs/MappingModal.js